import React, { useState, useEffect } from 'react';
import { Button } from '@design-system';
import { Logo, LogoType, Avatar, AvatarSize, BrandedAvatar } from '@design-system';
import { ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon, AddIcon } from '@chakra-ui/icons';
import { Tooltip, useMediaQuery } from '@chakra-ui/react';

import { useRouter } from 'hooks/useRouter';
import classNames from 'classnames';
import { useSession } from 'store/session';
import { Link } from 'react-router-dom';
const SafeLink = Link as any;
import { convertSize } from 'utils/convertSize';
import { SidebarItem } from './components/SidebarItem';
import { SidebarUserFooter } from './components/SidebarUserFooter';
import { GuestLoginModal } from './components/GuestLoginModal';
import jwtDecode from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { useFeature } from '@growthbook/growthbook-react';
import {
  MARKETING_FEATURE,
  REWARDS_FEATURE,
  FORMS_FEATURE,
} from 'constants/features';
import {
  getUserAvatarUrl,
  getTeamAvatarUrl,
  getDefaultUserAvatarUrl,
  getDefaultTeamAvatarUrl,
  generateGuestTeamAvatar,
} from 'pages/BrandingPage/services/brandingService';
import meta from '../../../meta.json';
import { getStorage, isGuestContext } from 'helpers/sessionContext';
import { isExternalVepormasGuest } from 'utils/isExternalVepormasGuest';
import { isVepormasHost } from 'utils/isVepormasHost';

const subscriptions = [
  {
    id: 'docs',
    title: translations.SIDEBAR.SUBS_CERT_SMART,
  },
  {
    id: 'docsBasic',
    title: translations.SIDEBAR.SUBS_CERT_BASIC,
  },
  {
    id: 'proof',
    title: translations.SIDEBAR.SUBS_PROOF,
  },
];

const getCount = (id, accountLimits) => {
  if (id === 'users') {
    return accountLimits.count_users;
  }
  if (id === 'docs') {
    return accountLimits.count_signed_documents;
  }
  if (id === 'docsBasic') {
    return (accountLimits as any)?.count_documents_vobo_nom151;
  }
  if (id === 'storage') {
    if (accountLimits.count_storage > 0) {
      return convertSize(accountLimits.count_storage);
    } else {
      return 0;
    }
  }
  if (id === 'proof') {
    let kycCount;
    const current = (accountLimits as any).count_kyc_validations;
    const maxKyc = (accountLimits as any).max_kyc_validations;
    const countKyc = (accountLimits as any).count_kyc_validations;
    if (countKyc > maxKyc) {
      kycCount = countKyc;
    } else kycCount = (countKyc - maxKyc) * -1;
    return current;
  }
};
const getLimit = (id, accountLimits) => {
  if (id === 'docs') {
    return accountLimits.max_signed_documents;
  }
  if (id === 'docsBasic') {
    return (accountLimits as any)?.max_documents_vobo_nom151;
  }
  if (id === 'proof') {
    const maxKyc = (accountLimits as any).max_kyc_validations;
    return maxKyc;
  }
};

const getProgress = (count, limit) => {
  return (count * 100) / limit;
};

const getPlan = (branding: boolean) => {
  if (branding) return 'Business';
  return 'Start';
};

const AccountSubscription = ({ t, accountLimits, credits, isLoggedIn = true }) => {
  const currentCredits =
    (credits?.docs?.got ?? 0) - (credits?.docs?.spent ?? 0);
  const currentKYC = (credits?.kyc?.got ?? 0) - (credits?.kyc?.spent ?? 0);

  return (
    <div className="flex flex-col">
      <p className="mt-4 text-paragraph-sm text-white">
        {t(translations.SIDEBAR.SUBS_PLAN)}:{' '}
        <strong>
          {isLoggedIn
            ? getPlan(accountLimits?.custom_branding_availability)
            : 'Invitado'}
        </strong>
      </p>

      <div className="mt-2">
        <div className="mb-1 flex flex-row items-center justify-between text-white">
          <p className="text-paragraph-sm">
            {t(translations.SIDEBAR.SUBS_CERT_SMART)}
          </p>
          <p className="text-paragraph-sm">{currentCredits}</p>
        </div>
      </div>
      <div className="mt-2">
        <div className="mb-1 flex flex-row items-center justify-between text-white">
          <p className="text-paragraph-sm">
            {t(translations.SIDEBAR.SUBS_PROOF)}
          </p>
          <p className="text-paragraph-sm">{currentKYC}</p>
        </div>
      </div>
    </div>
  );
};

export const Sidebar = ({
  primaryColor,
  logo,
  handleLogout,
  isOpen,
  setIsOpen,
}) => {
  const { t } = useTranslation();
  const {
    isLoggedIn,
    user,
    accountLimits,
    organizations,
    organizationImages,
    showBasicCertificates,
    team,
    credits,
  } = useSession();
  const router = useRouter();

  const [rawIsCollapsed, setRawIsCollapsed] = useState(() => {
    try {
      const saved = getStorage().getItem('sidebar_collapsed');
      return saved === 'true';
    } catch {
      return false;
    }
  });

  const [statsOpened, setStatsOpened] = useState(() => {
    try {
      const saved = getStorage().getItem('sidebar_stats_opened');
      if (saved === null) return true;
      return saved === 'true';
    } catch {
      return true;
    }
  });

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [lockedFeature, setLockedFeature] = useState('');
  const location = router.location; // router hook returns history object with location

  const guestUser = React.useMemo(() => {
    if (isLoggedIn) return null;
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    if (!token) return null;
    try {
      const decoded: any = jwtDecode(token);
      if (decoded && decoded.email) {
        return { email: decoded.email, name: decoded.email.split('@')[0] };
      }
    } catch {
      // Silently ignore invalid tokens - guest user check is best-effort
    }
    return null;
  }, [isLoggedIn, location.search]);

  // External guest = vepormas host + guest context + email not from internal corporate domains
  const isExternalVepormasGuestUser = !!(isVepormasHost() && guestUser && isGuestContext() && isExternalVepormasGuest(guestUser.email));

  const teamAvatarUrl = React.useMemo(() => {
    if (isLoggedIn && team?.uuid) return getTeamAvatarUrl(team.uuid);
    if (guestUser) {
      return generateGuestTeamAvatar(primaryColor);
    }
    return getDefaultTeamAvatarUrl();
  }, [isLoggedIn, team, guestUser, primaryColor]);

  const handleGuestAction = (feature: string) => {
    setLockedFeature(typeof feature === 'string' ? feature : 'acceder a funciones');
    setShowLoginModal(true);
  };

  const [isDesktop] = useMediaQuery('(min-width: 1024px)');
  // Force expanded state on mobile, otherwise respect user preference
  const isCollapsed = rawIsCollapsed && isDesktop;

  useEffect(() => {
    getStorage().setItem('sidebar_collapsed', String(rawIsCollapsed));
  }, [rawIsCollapsed]);

  useEffect(() => {
    getStorage().setItem('sidebar_stats_opened', String(statsOpened));
  }, [statsOpened]);

  // Auto-expand stats on mobile open
  // Auto-expand stats on mobile open
  useEffect(() => {
    if (isOpen && !isDesktop && !statsOpened) {
      setStatsOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isFormFeatureEnabled = useFeature(FORMS_FEATURE);

  const isMarketingFeatureEnabled = useFeature(MARKETING_FEATURE).on;

  const isRewardsFeatureEnabled = useFeature(REWARDS_FEATURE).on;

  const goToDocument = () => {
    router.history.push('/');
  };

  const getImage = (id) => {
    const currentImage = organizationImages.find((org) => org.id === id);

    if (currentImage) return currentImage.image;
    return getDefaultTeamAvatarUrl();
  };

  return (
    <>
      <div className="sidebar sidebarLeft flex">
        <div
          onClick={() => setIsOpen(false)}
          className={classNames(
            'fixed inset-0 z-20 bg-black opacity-50 transition-opacity lg:hidden',
            isOpen ? 'block' : 'hidden',
          )}
        />

        <div
          className={classNames(
            'fixed inset-y-0 left-0 z-30 flex w-64 transform flex-col overflow-hidden bg-black transition-all duration-300 lg:sticky lg:top-0 lg:h-full lg:translate-x-0',
            isOpen ? 'translate-x-0 ease-out' : '-translate-x-full ease-in',
            isCollapsed ? 'lg:w-20' : 'lg:w-64'
          )}
        >

          <div className="relative my-6 hidden flex-col items-center justify-center lg:flex lg:my-16">
            <div
              className="absolute top-[-30px] right-[-10px] cursor-pointer p-2 z-50 bg-black rounded-full border border-gray-700 hover:bg-gray-800 hidden" // Oculto por diseño previo, mejor ponerlo en un lugar visible
              onClick={() => setRawIsCollapsed(!rawIsCollapsed)}
            >
              {isCollapsed ? <ChevronRightIcon color="white" /> : <ChevronLeftIcon color="white" />}
            </div>

            {/* Toggle Button in Header Area (visible) */}
            <div className={classNames("absolute right-2 top-[-40px] hidden lg:block", isCollapsed ? "right-6" : "right-4")}>
              <button
                onClick={() => setRawIsCollapsed(!rawIsCollapsed)}
                className="text-white hover:text-gray-300 focus:outline-none p-1"
              >
                {isCollapsed ? <ChevronRightIcon w={6} h={6} /> : <ChevronLeftIcon w={6} h={6} />}
              </button>
            </div>

            <div className={classNames("flex items-center transition-all duration-300", isCollapsed ? "scale-75 justify-center" : "")}>
              {/* Brand */}
              {!isCollapsed ? (
                <Logo
                  onClick={() => router.push('/')}
                  wLogo={logo}
                  type={LogoType.white}
                  width="125px"
                  height="42px"
                />
              ) : (
                // Mostrar versin pequea o solo el logo si es posible, por ahora el logo completo escalado
                <div className="w-[40px] overflow-hidden justify-center flex">
                  <Logo
                    onClick={() => router.push('/')}
                    wLogo={logo}
                    type={LogoType.white}
                    width="125px"
                    height="42px"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="mb-4 hidden h-px w-full bg-gray-400 lg:block" />

          <div className="sidebarInner mb-4 flex-1 overflow-y-auto pt-10 lg:pt-0">
            {(isLoggedIn || (guestUser && !isExternalVepormasGuestUser)) && (
              <div className="block">
                <div className={classNames(
                  "flex flex-col pb-4 pt-1 text-xs no-underline lg:min-w-full",
                  isCollapsed ? "px-2 items-center" : "px-6"
                )}>
                  <div className={classNames("flex w-full", isCollapsed ? "justify-center" : "justify-between")}>
                    <div className={classNames("flex items-center min-w-0", isCollapsed ? "justify-center w-full" : "w-full")}>
                      <SafeLink
                        to={isLoggedIn ? { pathname: '/profile', search: `?tab=team&teamId=${team?.uuid}` } : '#'}
                        onClick={(e) => {
                          if (!isLoggedIn) {
                            e.preventDefault();
                            if (guestUser) handleGuestAction('Personalizar marca');
                          }
                        }}
                        className="group relative cursor-pointer block p-1 shrink-0"
                        title="Personalizar marca"
                      >
                        <BrandedAvatar
                          src={teamAvatarUrl}
                          alt="Profile Avatar"
                          size={AvatarSize.sm}
                          primaryColor={primaryColor}
                          showEditBadge
                        />
                      </SafeLink>
                      {!isCollapsed && (
                        <p className="ml-4 text-md text-white truncate" title={isLoggedIn ? team?.name : 'Invitado'}>
                          {isLoggedIn ? team?.name : 'Invitado'}
                        </p>
                      )}
                    </div>
                    {!isCollapsed && (
                      <div
                        className="relative top-1 ml-auto cursor-pointer"
                        onClick={() => setStatsOpened(!statsOpened)}
                      >
                        {!statsOpened ? (
                          <ChevronDownIcon color="#FFF" fontSize={24} />
                        ) : (
                          <ChevronUpIcon color="#FFF" fontSize={24} />
                        )}
                      </div>
                    )}
                  </div>
                  {!isCollapsed && statsOpened && (
                    <AccountSubscription
                      t={t}
                      accountLimits={accountLimits}
                      credits={credits}
                      isLoggedIn={isLoggedIn}
                    />
                  )}
                </div>
                {/* Divider */}
                {/* Divider */}
                <div className="mb-4 h-px w-full bg-gray-400" />
              </div>
            )}

            {/* Create Document Desktop */}
            {isLoggedIn && (
              <div className={classNames("px-4", isCollapsed ? "px-2 text-center" : "")}>
                {!isCollapsed ? (
                  <Button
                    className="mb-4 w-full text-white"
                    isFullWidth
                    color={primaryColor}
                    onClick={() => goToDocument()}
                    size="sm"
                    variant="solid"
                  >
                    {t(translations.SIDEBAR.CREATE_DOCUMENT)}
                  </Button>
                ) : (
                  // Botón + en modo colapsado
                  <Tooltip label={t(translations.SIDEBAR.CREATE_DOCUMENT) as string} placement="right">
                    <Button
                      className="mb-4 w-full text-white min-w-0 px-0"
                      color={primaryColor}
                      onClick={() => goToDocument()}
                      size="sm"
                      variant="solid"
                    >
                      <AddIcon w={4} h={4} />
                    </Button>
                  </Tooltip>
                )}
              </div>
            )}

            {(!isLoggedIn && guestUser && !isExternalVepormasGuestUser) && (
              <div className={classNames("px-4", isCollapsed ? "px-2 text-center" : "")}>
                {!isCollapsed ? (
                  <Button
                    className="mb-4 w-full text-white !opacity-50"
                    isFullWidth
                    color={primaryColor}
                    onClick={() => handleGuestAction('crear documentos')}
                    size="sm"
                    variant="solid"
                  >
                    {t(translations.SIDEBAR.CREATE_DOCUMENT)} <i className="fas fa-lock ml-2 text-xs" />
                  </Button>
                ) : (
                  <Tooltip label={t(translations.SIDEBAR.CREATE_DOCUMENT) as string} placement="right">
                    <Button
                      className="mb-4 w-full text-white min-w-0 px-0 !opacity-50"
                      color={primaryColor}
                      onClick={() => handleGuestAction('crear documentos')}
                      size="sm"
                      variant="solid"
                    >
                      <AddIcon w={4} h={4} />
                    </Button>
                  </Tooltip>
                )}
              </div>
            )}

            {!isLoggedIn && !guestUser && (
              <>
                <div className="md my-16 px-4 lg:hidden">
                  <p className="m-auto cursor-pointer text-center text-paragraph text-white">
                    <a href="/login">{t(translations.COMMON.LOGIN)}</a>
                  </p>
                  <Button
                    className="my-4 w-full text-white"
                    isFullWidth
                    color={primaryColor}
                    onClick={(e) => {
                      e?.preventDefault();
                      window.location.href = '/signup';
                    }}
                    size="sm"
                    variant="solid"
                  >
                    {t(translations.COMMON.REGISTER)}
                  </Button>
                </div>

                <div className="mb-4 h-px w-full bg-gray-400 lg:hidden" />
              </>
            )}

            <nav className="mt-2">
              {isLoggedIn && (
                <>
                  <SidebarItem
                    isCollapsed={isCollapsed}
                    borderColor={primaryColor}
                    dropdown
                    isOpen={
                      router.pathname.includes('users') &&
                      router.pathname.includes('documents')
                    }
                    label={t(translations.SIDEBAR.INBOX)}
                    id="inbox"
                    link={`/users/${user?.id}/documents`}
                    isActive={
                      router.pathname.includes('users') &&
                      router.pathname.includes('documents')
                    }
                    items={
                      [
                        {
                          link: `/users/${user?.id}/documents?status=unsigned,partially_signed&invite_type=signer`,
                          label: t(translations.SIDEBAR.INBOX_IN_PROGRESS),
                          active: 'status.unsigned,partially_signed',
                        },
                        {
                          link: `/users/${user?.id}/documents?status=signed&invite_type=signer`,
                          label: t(translations.SIDEBAR.INBOX_SIGNED),
                          active: 'status.signed',
                        },
                        {
                          link: `/users/${user?.id}/documents?invite_type=observer`,
                          label: t(translations.SIDEBAR.INBOX_INVITED_TO_WATCH),
                          active: 'invite_type.observer',
                        },
                        {
                          link: `/users/${user?.id}/documents`,
                          label: t(translations.SIDEBAR.INBOX_ALL),
                        },
                      ] as any
                    }
                  />
                  <SidebarItem
                    isCollapsed={isCollapsed}
                    borderColor={primaryColor}
                    label={t(translations.SIDEBAR.ORGANIZATIONS)}
                    id="teams"
                    link="/teams"
                    isActive={
                      router.pathname.includes('teams') &&
                      !router.pathname.includes('folders') &&
                      !router.pathname.includes('contacts')
                    }
                  />
                  <SidebarItem
                    isCollapsed={isCollapsed}
                    borderColor={primaryColor}
                    label={t(translations.SIDEBAR.FOLDERS)}
                    id="carpetas"
                    link={`/teams/${team?.uuid}/folders`}
                    isActive={router.pathname.includes('folders')}
                  />
                  <SidebarItem
                    isCollapsed={isCollapsed}
                    borderColor={primaryColor}
                    label={t(translations.SIDEBAR.CONTACTS)}
                    id="contacts"
                    link={`/teams/${team?.uuid}/contacts`}
                    isActive={router.pathname.includes('contacts')}
                  />
                </>
              )}


              {(!isLoggedIn && guestUser && !isExternalVepormasGuestUser) && (
                <>
                  <SidebarItem
                    isCollapsed={isCollapsed}
                    borderColor={primaryColor}
                    label={t(translations.SIDEBAR.INBOX)}
                    id="inbox"
                    link="#"
                    isLocked
                    onClick={() => handleGuestAction('acceder a tu bandeja')}
                  />
                  <SidebarItem
                    isCollapsed={isCollapsed}
                    borderColor={primaryColor}
                    label={t(translations.SIDEBAR.ORGANIZATIONS)}
                    id="teams"
                    link="#"
                    isLocked
                    onClick={() => handleGuestAction('gestionar equipos')}
                  />
                  <SidebarItem
                    isCollapsed={isCollapsed}
                    borderColor={primaryColor}
                    label={t(translations.SIDEBAR.FOLDERS)}
                    id="carpetas"
                    link="#"
                    isLocked
                    onClick={() => handleGuestAction('organizar carpetas')}
                  />
                  <SidebarItem
                    isCollapsed={isCollapsed}
                    borderColor={primaryColor}
                    label={t(translations.SIDEBAR.CONTACTS)}
                    id="contacts"
                    link="#"
                    isLocked
                    onClick={() => handleGuestAction('ver tus contactos')}
                  />
                </>
              )}
              <SidebarItem
                isCollapsed={isCollapsed}
                borderColor={primaryColor}
                label={t(translations.SIDEBAR.VERIFICATION)}
                id="verificacion"
                link="https://audit.cincel.digital"
                isExternal
                isActive={router.pathname.includes('verificacion')}
              />

              {isMarketingFeatureEnabled && user?.role === 'marketing' ? (
                <SidebarItem
                  isCollapsed={isCollapsed}
                  borderColor={primaryColor}
                  dropdown
                  label={t(translations.SIDEBAR.MARKETING)}
                  id="admin"
                  isActive={router.pathname.includes('marketing')}
                  items={
                    [
                      {
                        link: '/marketing',
                        label: t(translations.COMMON.CREATE),
                      },
                      {
                        link: '/marketing/rewards',
                        label: t(translations.SIDEBAR.MARKETING_REWARDS),
                      },
                    ] as any
                  }
                />
              ) : null}

            </nav>
          </div>

          {/* User Footer - Todas las pantallas */}
          <SidebarUserFooter
            user={user}
            isLoggedIn={isLoggedIn}
            isCollapsed={isCollapsed}
            handleLogout={handleLogout}
            primaryColor={primaryColor}
            onGuestLogin={isExternalVepormasGuestUser ? undefined : () => setShowLoginModal(true)}
          />

          {guestUser && !isExternalVepormasGuestUser && (
            <GuestLoginModal
              isOpen={showLoginModal}
              onClose={() => setShowLoginModal(false)}
              email={guestUser.email}
              name={guestUser.name}
              primaryColor={primaryColor}
              featureName={lockedFeature}
            />
          )}

        </div>
      </div>
    </>
  );
};
